import { BusinessObjectBase } from './base';
import { processInstanceHistorySchema } from '@schemas/business-objects/process-instance-history';
import type { ProcessInstance } from './process-instance';
import type { Tenant } from './tenant';
import type {
  HistoryData,
  Id,
  ProcessInstanceHistoryEventType,
} from '../base-types';

export interface ProcessInstanceHistoryData {
  readonly id: Id;
  readonly eventType: ProcessInstanceHistoryEventType;
  readonly data?: HistoryData;
  readonly timestamp?: Date;
  readonly processInstanceId: Id;
  readonly processInstance?: ProcessInstance;
  readonly tenantId: Id;
  readonly tenant?: Tenant;
}

export interface ProcessInstanceHistory extends Readonly<ProcessInstanceHistoryData> {}
type ProcessInstanceHistoryCreateWithChildrenData =
  Partial<ProcessInstanceHistoryData>;

export class ProcessInstanceHistory extends BusinessObjectBase {
  static readonly entityName = 'ProcessInstanceHistory' as const;
  static readonly plural = 'process-instance-histories';
  static schema = processInstanceHistorySchema;

  // --- Static CRUD methods ---

  static async find(filter?: any): Promise<ProcessInstanceHistory[]> {
    const url = `${this.baseUrl}/${this.plural}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const results = await this.get<any[]>(url, params);
    return results.map((data) => new ProcessInstanceHistory(data));
  }

  static async findOne(filter?: any): Promise<ProcessInstanceHistory | null> {
    const url = `${this.baseUrl}/${this.plural}/find-one`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params);
    return data ? new ProcessInstanceHistory(data) : null;
  }

  static async findById(id: Id, filter?: any): Promise<ProcessInstanceHistory> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params);
    return new ProcessInstanceHistory(data);
  }

  static async create(
    data: Omit<ProcessInstanceHistoryData, 'id'>,
  ): Promise<ProcessInstanceHistory> {
    const url = `${this.baseUrl}/${this.plural}`;
    const result = await this.post<any>(url, data);
    return new ProcessInstanceHistory(result);
  }

  static async createMany(
    data: Omit<ProcessInstanceHistoryData, 'id'>[],
  ): Promise<ProcessInstanceHistory[]> {
    const url = `${this.baseUrl}/${this.plural}/create-many`;
    const results = await this.post<any[]>(url, data);
    return results.map((item: any) => new ProcessInstanceHistory(item));
  }

  static async createWithChildren(
    data: ProcessInstanceHistoryCreateWithChildrenData,
  ): Promise<ProcessInstanceHistory> {
    const url = `${this.baseUrl}/${this.plural}/create-with-children`;
    const result = await this.post<any>(url, data);
    return new ProcessInstanceHistory(result);
  }

  static async createManyWithChildren(
    data: ProcessInstanceHistoryCreateWithChildrenData[],
  ): Promise<ProcessInstanceHistory[]> {
    const url = `${this.baseUrl}/${this.plural}/create-many-with-children`;
    const results = await this.post<any[]>(url, data);
    return results.map((item: any) => new ProcessInstanceHistory(item));
  }

  static async updateById(
    id: Id,
    updates: Partial<ProcessInstanceHistoryData>,
  ): Promise<ProcessInstanceHistory> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    const result = await this.patch<any>(url, updates);
    return new ProcessInstanceHistory(result);
  }

  static async deleteById(id: Id): Promise<boolean> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    try {
      await this.del<void>(url);
      return true;
    } catch {
      return false;
    }
  }
}
